'use client';

import { SideMenuItem } from '@repo/ui/side-menu-item';
import { useQuery } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import SimpleListSkeleton from '@/components/common/skeletons/simple-list-skeleton';
import { renderIcon } from '@/components/layout/sidebar-left/render-icon';
import { usePathname, useRouter } from '@/i18n/routing';
import { getCasinoGamesTypes } from '@/lib/fetch/casino-games/utils';
import { getGamesCategoryItems } from '@/lib/utils/casino-categories';
import { useTenant } from '@/providers/TenantProvider';

type GameCategoriesProps = {
  sidebarOpen: boolean;
};

const GameCategories = ({ sidebarOpen }: GameCategoriesProps) => {
  const t = useTranslations('casino');
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  const { tenantMachineName } = useTenant();

  const { data = [], isLoading } = useQuery({
    queryKey: ['gameTypeKeys'],
    queryFn: () => getCasinoGamesTypes(tenantMachineName),
  });

  const categories = getGamesCategoryItems(t, data, locale);

  if (isLoading) {
    return (
      <SimpleListSkeleton
        itemsCount={13}
        className="mb-2 space-y-1"
        itemClassName="h-10 w-full"
      />
    );
  }

  return (
    <div data-testid="game-categories-vertical-list">
      {categories.map((item) => {
        const { name, href, Icon, matches, notMatches, count } = item;
        const currentPath = pathname === '/casino' ? '/casino/lobby' : pathname;
        const isActive = !notMatches?.includes(currentPath) && matches?.includes(currentPath);

        return (
          <SideMenuItem
            key={href}
            variant="secondary"
            className="rounded-none"
            text={sidebarOpen && name}
            Icon={renderIcon(Icon)}
            isActive={isActive}
            optionalLastElement={sidebarOpen && <span>{count || 0}</span>}
            onClick={() => router.push(href)}
            data-testid="game-categories-vertical-list-item"
            tooltipText={name}
            showTooltip={!sidebarOpen}
          />
        );
      })}
    </div>
  );
};
export default GameCategories;
